//angular references
import { Component, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

//BusinessEvent control references
import { BEDataService } from '../../services/businessEvent/beData.service';
import { BEWorkflowSteps } from '../../services/businessEvent/beWorkflow.model';

//Era model references
import { BusinessEvent, enumBusinessEventAction, enumBusinessEventMode } from '../../models/businessevent.model';
import { Identification, enumIdentType, enumIdentStatus } from '../../models/identification.model';

//OTA model references
import { OTA_Request, enumRequestType } from '../../models/ota_request.model';
import { OTA_Response } from '../../models/ota_response.model';
import { OTA_Telephone } from '../../models/ota_telephone.model';
import { OTA_Email } from '../../models/ota_email.model';
import { OTA_Document } from '../../models/ota_document.model';
import { OTA_SpecialEquipPref } from '../../models/ota_specialequippref.model';
import { OTA_CoveragePref } from '../../models/ota_coveragepref.model';
import { OTA_Error } from '../../models/ota_error.model';
import { enumCallStatus } from '../../models/callStatus.model';
import { enumPromotionType } from '../../models/promotiontype.model';
import { OTA_VehAvail } from '../../models/ota_vehavail.model';
import { OTA_PricedEquip } from '../../models/ota_pricedequip.model';
import { OTA_PricedCoverage } from '../../models/ota_pricedcoverage.model';
import { OTA_Charge } from '../../models/ota_charge.model';
import { CurrencyAmount } from '../../models/currencyamount.model';
import { ConfigDataService, enumClientConfig, enumUIConfig } from '../../services/configuration/configData.service';
import { DictionaryService } from '../../services/dictionary/dictionary.service';
import { OTA_Fee } from '../../models/ota_fee.model';
import { OTA_OffLocService } from '../../models/ota_offlocservice.model';
import { KeyValuePair } from '../../models/keyvaluepair.model';
import { PortalDictionary } from '../../models/portaldictionary.model';
import { OTA_Address } from '../../models/ota_address.model';

enum enumAsyncLoad {
    OTAResponse = 1,
    UIConfigs = 2,
    ClientConfigs = 3,
    Dictionary = 4,
};

@Component({
    selector: 'beConfirmation',
    template: require('./beConfirmation.component.html'),
    styles: [require('../app/app.component.css')]
})

export class BEConfirmationComponent {
    private _component = "beConfirmation";

    //page/workflow
    private _title = "Your booking is confirmed";
    private _title_default = "Your booking is confirmed";
    private _title_onRequest = "Your booking request is confirmed";
    private _currentStep = BEWorkflowSteps.beConfirmation;
    private _firstStep = "";
    private _previousStep = "";
    private _errorHeader: string = "Unable to make the booking";
    private _showCosts: boolean = true;
    private _baseVehImgURL = "/img/vehicleImages/";
    private _showNotificationMessage: boolean = false;

    //loaded 
    private _fullyLoaded: boolean = false;
    private _loadingErrorMsgs: string[] = new Array();
    private _loaded = [
        {
            step: enumAsyncLoad.OTAResponse,
            loaded: false,
        },
        {
            step: enumAsyncLoad.UIConfigs,
            loaded: false,
        },
        {
            step: enumAsyncLoad.ClientConfigs,
            loaded: false,
        },
        {
            step: enumAsyncLoad.Dictionary,
            loaded: false,
        }
    ];

    private _font: string = "";
    private _textColour: string = "";
    private _invalidTextColour: string = "";
    private _invalidBorderColour: string = "";
    private _containerBackColour: string = "";
    private _containerBorderColour: string = "";
    private _containerCornerRadius: string = "";
    private _inactiveTabColour: string = "";
    private _containerBorderFull: string = "";

    private _locationOutTextColour: string = "";
    private _locationOutBorderColour: string = "";
    private _locationInTextColour: string = "";
    private _locationInBorderColour: string = "";
    private _dateTimeOutTextColour: string = "";
    private _dateTimeOutBorderColour: string = "";
    private _dateTimeInTextColour: string = "";
    private _dateTimeInBorderColour: string = "";
    private _ageRangeTextColour: string = "";
    private _ageRangeBorderColour: string = "";
    private _bookingRefTextColour: string = "";
    private _bookingRefBorderColour: string = "";
    private _emailRefTextColour: string = "";
    private _emailRefBorderColour: string = "";

    private _commandButtonFont: string = "";
    private _commandButtonRadius: string = "";
    private _commandButtonTextSize: string = "";
    private _commandButtonTextWeight: string = "";
    private _primaryCommandButtonTextColour: string = "";
    private _primaryCommandButtonBackColour: string = "";
    private _primaryCommandButtonBorderFull: string = "";
    private _secondaryCommandButtonTextColour: string = "";
    private _secondaryCommandButtonBackColour: string = "";
    private _secondaryCommandButtonBorderFull: string = "";

    //business event parameters
    private _be: BusinessEvent;
    private _otaResponse: OTA_Response;
    private _confID: string;
    private _driverFirstName: string;
    private _driverLastName: string;
    private _driverEmail: string;
    private _driverPhoneNumber: string;
    private _locationOut: string = "";
    private _locationIn: string = "";
    private _dateTimeOut: string;
    private _dateTimeIn: string;
    private _selectedVehicle: OTA_VehAvail;
    private _otaPricedCoverage: OTA_PricedCoverage;
    private _arrivalNo: string = "";
    private _deliveryAddress: string = "";
    private _collectionAddress: string = "";

    //lists
    private _otaPricedEquips: OTA_PricedEquip[];
    private _otaPricedCoverages: OTA_PricedCoverage[];
    private _otaCharges: OTA_Charge[];
    private _otaFees: OTA_Fee[];
    private _totalCosts = new Array<CurrencyAmount>();
    private _otaFeesNotIncluded: OTA_Fee[];

    //
    //component events
    //
    public constructor(private _router: Router,
        private _beDataService: BEDataService,
        private _configDataService: ConfigDataService,
        private _dictionaryService: DictionaryService,
        private _httpClient: HttpClient,
        @Inject('BASE_URL') private _baseUrl: string)
    {
        this._firstStep = this._beDataService.firstStep();
        this._previousStep = this._beDataService.previousStep(this._currentStep);
    }

    private async ngOnInit() {
        //get the current BusinessEvent's Confirmation data
        this._be = this._beDataService.getBusinessEventForStep(this._currentStep);

        //get the OTA Available Vehicles for the current BusinessEvent
        if (this._be.eventMode == enumBusinessEventMode.beMode_View) {
            //not actually performing any action, just showing the booking details
            this.asyncLoaded(enumAsyncLoad.OTAResponse);
        }
        else {
            //construct the OTA Request object for the current business event
            let otaReq: OTA_Request = this.createOTARequest();

            this._httpClient.get<OTA_Response>(this._baseUrl + 'api/OTAData/OTARequest?'
                + '&otaReqJSON=' + JSON.stringify(otaReq)).subscribe(result => {
                    this._otaResponse = result;
                    this.asyncLoaded(enumAsyncLoad.OTAResponse);
                }, error => console.error(error));
        }

        //get the configured UIConfigs
        if (this._configDataService.uiConfigs == null) {
            this._httpClient.get<KeyValuePair[]>(this._baseUrl + 'api/ConfigData/UIConfigs').subscribe(result => {
                this._configDataService.uiConfigs = result;
                this.asyncLoaded(enumAsyncLoad.UIConfigs);
            }, error => console.error(error));
        }
        else
            this.asyncLoaded(enumAsyncLoad.UIConfigs);

        //get the configured ClientConfigs
        if (this._configDataService.clientConfigs == null) {
            this._httpClient.get<KeyValuePair[]>(this._baseUrl + 'api/ConfigData/ClientConfigs').subscribe(result => {
                this._configDataService.clientConfigs = result;
                this.asyncLoaded(enumAsyncLoad.ClientConfigs);
            }, error => console.error(error));
        }
        else
            this.asyncLoaded(enumAsyncLoad.ClientConfigs);

        //get the configured Dictionary
        if (this._dictionaryService.dictionaries == null) {
            this._httpClient.get<PortalDictionary[]>(this._baseUrl + 'api/DictionaryData/PortalDictionaries').subscribe(result => {
                this._dictionaryService.dictionaries = result;
                this.asyncLoaded(enumAsyncLoad.Dictionary);
            }, error => console.error(error));
        }
        else
            this.asyncLoaded(enumAsyncLoad.Dictionary);
    }

    //
    //component management
    //
    private asyncLoaded(step: enumAsyncLoad) {
        let fullyLoaded: boolean = true;

        for (var i = 0; i < this._loaded.length; i++) {
            if (this._loaded[i].step === step) {
                //record the given step as being loaded
                this._loaded[i].loaded = true;
            }
            else if (!this._loaded[i].loaded) {
                //check if all async steps have been loaded
                fullyLoaded = false;
            }
        }

        switch (step) {
            case enumAsyncLoad.UIConfigs:
                let font: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_Font);
                let textColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_TextColour);
                let invalidTextColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_InvalidTextColour);
                let invalidBorderColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_InvalidControlBorderColour);
                let containerBackColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_ContainerBackgroundColour);
                let containerBorderColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_ContainerBorderColour);
                let containerCornerRadius: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_ContainerCornerRadius);
                let inactiveTabColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_InactiveTabColour);
                let containerBorderStyle: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_ContainerBorderStyle);

                let commandButtonFont: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_CommandButtonFont);
                let commandButtonRadius: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_CommandButtonRadius);
                let commandButtonTextSize: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_CommandButtonTextSize);
                let commandButtonTextWeight: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_CommandButtonTextWeight);
                let primaryCommandButtonTextColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_PrimaryCommandButtonTextColour);
                let primaryCommandButtonBackColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_PrimaryCommandButtonBackgroundColour);
                let primaryCommandButtonBorderColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_PrimaryCommandButtonBorderColour);
                let primaryCommandButtonBorderStyle: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_PrimaryCommandButtonBorderStyle);

                let secondaryCommandButtonTextColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_SecondaryCommandButtonTextColour);
                let secondaryCommandButtonBackColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_SecondaryCommandButtonBackgroundColour);
                let secondaryCommandButtonBorderColour: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_SecondaryCommandButtonBorderColour);
                let secondaryCommandButtonBorderStyle: string = this._configDataService.getUIConfig(enumUIConfig.uiconf_SecondaryCommandButtonBorderStyle);

                if (font.trim().length > 0) {
                    this._font = font;
                }
                if (textColour.trim().length > 0) {
                    this._textColour = textColour;
                }
                if (invalidTextColour.trim().length > 0) {
                    this._invalidTextColour = invalidTextColour;
                }
                if (invalidBorderColour.trim().length > 0) {
                    this._invalidBorderColour = invalidBorderColour;
                }
                if (containerBackColour.trim().length > 0) {
                    this._containerBackColour = containerBackColour;
                }
                if (containerBorderColour.trim().length > 0) {
                    this._containerBorderColour = containerBorderColour;
                }
                if (containerCornerRadius.trim().length > 0) {
                    this._containerCornerRadius = containerCornerRadius;
                }
                if (inactiveTabColour.trim().length > 0) {
                    this._inactiveTabColour = inactiveTabColour;
                }
                if ((containerBorderStyle.trim().length > 0)
                    || (containerBorderColour.trim().length > 0)) {
                    this._containerBorderFull = containerBorderColour + " " + containerBorderStyle;
                }

                if (commandButtonFont.trim().length > 0) {
                    this._commandButtonFont = commandButtonFont;
                }
                if (commandButtonRadius.trim().length > 0) {
                    this._commandButtonRadius = commandButtonRadius;
                }
                if (commandButtonTextSize.trim().length > 0) {
                    this._commandButtonTextSize = commandButtonTextSize;
                }
                if (commandButtonTextWeight.trim().length > 0) {
                    this._commandButtonTextWeight = commandButtonTextWeight;
                }
                if (primaryCommandButtonTextColour.trim().length > 0) {
                    this._primaryCommandButtonTextColour = primaryCommandButtonTextColour;
                }
                if (primaryCommandButtonBackColour.trim().length > 0) {
                    this._primaryCommandButtonBackColour = primaryCommandButtonBackColour;
                }
                if ((primaryCommandButtonBorderStyle.trim().length > 0)
                    || (primaryCommandButtonBorderColour.trim().length > 0)) {
                    this._primaryCommandButtonBorderFull = primaryCommandButtonBorderColour + " " + primaryCommandButtonBorderStyle;
                }
                if (secondaryCommandButtonTextColour.trim().length > 0) {
                    this._secondaryCommandButtonTextColour = secondaryCommandButtonTextColour;
                }
                if (secondaryCommandButtonBackColour.trim().length > 0) {
                    this._secondaryCommandButtonBackColour = secondaryCommandButtonBackColour;
                }
                if ((secondaryCommandButtonBorderStyle.trim().length > 0)
                    || (secondaryCommandButtonBorderColour.trim().length > 0)) {
                    this._secondaryCommandButtonBorderFull = secondaryCommandButtonBorderColour + " " + secondaryCommandButtonBorderStyle;
                }

                break;

            case enumAsyncLoad.ClientConfigs:
                var baseVehImgURL = "/img/vehicleImages/";
                let configVehImgURL: string = this._configDataService.getClientConfig(enumClientConfig.cconf_VehicleImageFolder, baseVehImgURL);
                if (configVehImgURL.trim().length > 0) {
                    this._baseVehImgURL = configVehImgURL;
                }

                this._showCosts = (this._configDataService.getClientConfig(enumClientConfig.cconf_NonResConfirmationShowCosts) == "1");
                this._showNotificationMessage = (this._configDataService.getClientConfig(enumClientConfig.cconf_HasNotifications) == "1");

                break;

            case enumAsyncLoad.Dictionary:
                this._title_default = this._dictionaryService.getDictionaryValue(this._component, "Title-Default", false, "Your booking is confirmed");
                this._title_onRequest = this._dictionaryService.getDictionaryValue(this._component, "Title-OnRequest", false, "Your booking request is confirmed");
                break;
        }

        this._fullyLoaded = fullyLoaded;

        if (fullyLoaded) {
            //get the current BusinessEvent's data
            this.loadBusinessEvent();
        }
    }

    private loadBusinessEvent() {
        if (this._be.eventMode == enumBusinessEventMode.beMode_View) {
            //not actually performing any action, just showing the booking details
            this.loadBusinessEvent_View();
        }
        else {
            switch (this._be.eventAction) {
                case enumBusinessEventAction.beAction_Create:
                    this.loadBusinessEvent_Create();
                    break;
                case enumBusinessEventAction.beAction_Update:
                    this.loadBusinessEvent_Update();
                    break;
                case enumBusinessEventAction.beAction_Cancel:
                    this.loadBusinessEvent_Cancel();
                    break;
            }
        }

        //ensure we're positioned at the top of the page
        window.scroll(0, 0);
    }

    private loadBusinessEvent_Create() {
        //this._title = 'Your booking is confirmed';
        this._title = this._title_default;
        
        let errors: OTA_Error[] = new Array<OTA_Error>();

        if (this._otaResponse == null) {
            let otaError: OTA_Error = new OTA_Error();
            otaError.shortText = "No response received";
            errors.push(otaError);
        }
        else {
            if ((this._otaResponse.callStatus != null)
                && (this._otaResponse.callStatus.code != enumCallStatus.csStatus_OK))
            {
                let otaError: OTA_Error = new OTA_Error();
                otaError.shortText = "Error - " + this._otaResponse.callStatus.code.toString();
                //TODO specific messages per CallStatus code?
                errors.push(otaError);
            }
            else {
                if ((this._otaResponse.otA_VehResRS.errors != null)
                    && (this._otaResponse.otA_VehResRS.errors.length > 0)) {
                    errors = this._otaResponse.otA_VehResRS.errors;
                }
            }
        }

        if (errors.length > 0) {
            //errors, so inform the user, and take them back to the previous page
            this._errorHeader = "Unable to make the booking";

            for (var otaErr in errors) {
                this._loadingErrorMsgs.push(errors[otaErr].shortText);
            }
        }
        else {
            //no errors
            this._title = this._title_default;
            if (this._be.otaVehAvail.vehAvailCore.status == "OnRequest")
                this._title = this._title_onRequest;

            this._locationOut = this._be.locationOut.description;
            this._locationIn = this._be.locationIn.description;
            this._deliveryAddress = this._be.deliveryAddress;
            this._collectionAddress = this._be.collectionAddress;

            var dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
            var timeOptions = { hour: 'numeric', minute: '2-digit' };
            this._dateTimeOut = ((this._be.dateTimeOut == null) ? "<unknown>" :
                this._be.dateTimeOut.toLocaleDateString(navigator.language, dateOptions)
                + " at " + this._be.dateTimeOut.toLocaleTimeString(navigator.language, timeOptions))
            this._dateTimeIn = ((this._be.dateTimeIn == null) ? "<unknown>" :
                this._be.dateTimeIn.toLocaleDateString(navigator.language, dateOptions)
                + " at " + this._be.dateTimeIn.toLocaleTimeString(navigator.language, timeOptions));

            this._be.locationOut.isAirport = this._be.locationOut.isAirport;
            this._arrivalNo = this._be.flightNumber;

            this._selectedVehicle = this._be.otaVehAvail;
            this._otaPricedEquips = this._be.otaVehAvail.vehAvailCore.pricedEquips.filter(eq => eq.charge.includedInEstTotalInd);
            this._otaPricedCoverages = this._be.otaVehAvail.vehAvailInfo.pricedCoverages;
            this._otaCharges = this._be.otaVehAvail.vehAvailCore.rentalRate.vehCharges;
            this._otaFees = this._be.otaVehAvail.vehAvailCore.fees.filter(f => f.includedInEstTotalInd);
            this._otaFeesNotIncluded = this._be.otaVehAvail.vehAvailCore.fees.filter(f => ((!f.includedInEstTotalInd) && (this._configDataService.showAdditionalFee(f))));

            //get the currently selected Coverage
            if (this._otaPricedCoverages.length > 0) {
                for (var i = 0; i < this._otaPricedCoverages.length; i++) {
                    let cvr = this._otaPricedCoverages[i];

                    if ((cvr.charge.includedInEstTotalInd) && (!cvr.charge.includedInRate)) {
                        this._otaPricedCoverage = cvr;
                        break;
                    }
                    else if (cvr.charge.includedInRate)
                        this._otaPricedCoverage = cvr;
                }
            }

            //this.totalCurrencyCost(this._be.otaVehAvail.vehAvailCore.totalCharge.currencyCode, this._be.otaVehAvail.vehAvailCore.totalCharge.estimatedTotalAmount);
            var ttlAmount = this._be.otaVehAvail.vehAvailCore.totalCharge.estimatedTotalAmount;
            if (this._be.payNow) {
                for (var chrg in this._otaCharges) {
                    if (this._otaCharges[chrg].calculations != null) {
                        this._otaCharges[chrg].calculations.forEach(function (calc) {
                            ttlAmount = ttlAmount - (calc.total * 0.1);
                        });
                    }
                }
            }
            this.totalCurrencyCost(this._be.otaVehAvail.vehAvailCore.totalCharge.currencyCode, ttlAmount);


            this._driverFirstName = this._be.rentingentity.firstName;
            this._driverLastName = this._be.rentingentity.principalName;
            //get the valid email Identification
            var eml = this._be.rentingentity.identList.find(x => ((x.identType == enumIdentType.idType_Email) && (x.identStatus == enumIdentStatus.idStatus_Valid)));
            if (eml != null) {
                this._driverEmail = eml.membershipNumber;
            }
            //get the Phone Number
            if (this._be.rentingentity.phoneList.length > 0) {
                this._driverPhoneNumber = this._be.rentingentity.phoneList[0].phoneNumber;
            }

            this._confID = this._otaResponse.otA_VehResRS.vehResRSCore.vehReservation.vehSegmentCore.confID.confID;
        }
    }

    private loadBusinessEvent_Update() {
        this._title = "Your booking has been updated";

        let errors: OTA_Error[] = new Array<OTA_Error>();

        if (this._otaResponse == null) {
            let otaError: OTA_Error = new OTA_Error();
            otaError.shortText = "No response received";
            errors.push(otaError);
        }
        else {
            if ((this._otaResponse.callStatus != null)
                && (this._otaResponse.callStatus.code != enumCallStatus.csStatus_OK)) {
                let otaError: OTA_Error = new OTA_Error();
                otaError.shortText = "Error - " + this._otaResponse.callStatus.code.toString();
                //TODO specific messages per CallStatus code?
                errors.push(otaError);
            }
            else {
                if ((this._otaResponse.otA_VehModifyRS.errors != null)
                    && (this._otaResponse.otA_VehModifyRS.errors.length > 0)) {
                    errors = this._otaResponse.otA_VehModifyRS.errors;
                }
            }
        }

        if (errors.length > 0) {
            //errors, so inform the user, and take them back to the previous page
            this._errorHeader = "Unable to modify the booking";

            for (var otaErr in errors) {
                this._loadingErrorMsgs.push(errors[otaErr].shortText);
            }
        }
        else {
            //no errors

            this._locationOut = this._be.locationOut.description;
            this._locationIn = this._be.locationIn.description;
            this._deliveryAddress = this._be.deliveryAddress;
            this._collectionAddress = this._be.collectionAddress;

            var dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
            var timeOptions = { hour: 'numeric', minute: '2-digit' };
            this._dateTimeOut = ((this._be.dateTimeOut == null) ? "<unknown>" :
                this._be.dateTimeOut.toLocaleDateString(navigator.language, dateOptions)
                + " at " + this._be.dateTimeOut.toLocaleTimeString(navigator.language, timeOptions))
            this._dateTimeIn = ((this._be.dateTimeIn == null) ? "<unknown>" :
                this._be.dateTimeIn.toLocaleDateString(navigator.language, dateOptions)
                + " at " + this._be.dateTimeIn.toLocaleTimeString(navigator.language, timeOptions));

            this._be.locationOut.isAirport = this._be.locationOut.isAirport;
            this._arrivalNo = this._be.flightNumber;

            this._selectedVehicle = this._be.otaVehAvail;
            this._otaPricedEquips = this._be.otaVehAvail.vehAvailCore.pricedEquips.filter(eq => eq.charge.includedInEstTotalInd);
            this._otaPricedCoverages = this._be.otaVehAvail.vehAvailInfo.pricedCoverages;
            this._otaCharges = this._be.otaVehAvail.vehAvailCore.rentalRate.vehCharges;
            this._otaFees = this._be.otaVehAvail.vehAvailCore.fees.filter(f => f.includedInEstTotalInd);
            this._otaFeesNotIncluded = this._be.otaVehAvail.vehAvailCore.fees.filter(f => ((!f.includedInEstTotalInd) && (this._configDataService.showAdditionalFee(f))));

            //get the currently selected Coverage
            if (this._otaPricedCoverages.length > 0) {
                for (var i = 0; i < this._otaPricedCoverages.length; i++) {
                    let cvr = this._otaPricedCoverages[i];

                    if ((cvr.charge.includedInEstTotalInd) && (!cvr.charge.includedInRate)) {
                        this._otaPricedCoverage = cvr;
                        break;
                    }
                    else if (cvr.charge.includedInRate)
                        this._otaPricedCoverage = cvr;
                }
            }

            //this.totalCurrencyCost(this._be.otaVehAvail.vehAvailCore.totalCharge.currencyCode, this._be.otaVehAvail.vehAvailCore.totalCharge.estimatedTotalAmount);
            var ttlAmount = this._be.otaVehAvail.vehAvailCore.totalCharge.estimatedTotalAmount;
            if (this._be.payNow) {
                for (var chrg in this._otaCharges) {
                    if (this._otaCharges[chrg].calculations != null) {
                        this._otaCharges[chrg].calculations.forEach(function (calc) {
                            ttlAmount = ttlAmount - (calc.total * 0.1);
                        });
                    }
                }
            }
            this.totalCurrencyCost(this._be.otaVehAvail.vehAvailCore.totalCharge.currencyCode, ttlAmount);

            this._driverFirstName = this._be.rentingentity.firstName;
            this._driverLastName = this._be.rentingentity.principalName;
            //get the valid email Identification
            var eml = this._be.rentingentity.identList.find(x => ((x.identType == enumIdentType.idType_Email) && (x.identStatus == enumIdentStatus.idStatus_Valid)));
            if (eml != null) {
                this._driverEmail = eml.membershipNumber;
            }
            //get the Phone Number
            if (this._be.rentingentity.phoneList.length > 0) {
                this._driverPhoneNumber = this._be.rentingentity.phoneList[0].phoneNumber;
            }

            this._confID = this._otaResponse.otA_VehModifyRS.vehModifyRSCore.vehReservation.vehSegmentCore.confID.confID;
        }
    }

    private loadBusinessEvent_Cancel() {
        this._title = "Your booking is cancelled";

        let errors: OTA_Error[] = new Array<OTA_Error>();

        if (this._otaResponse == null) {
            let otaError: OTA_Error = new OTA_Error();
            otaError.shortText = "No response received";
            errors.push(otaError);
        }
        else {
            if ((this._otaResponse.callStatus != null)
                && (this._otaResponse.callStatus.code != enumCallStatus.csStatus_OK)) {
                let otaError: OTA_Error = new OTA_Error();
                otaError.shortText = "Error - " + this._otaResponse.callStatus.code.toString();
                //TODO specific messages per CallStatus code?
                errors.push(otaError);
            }
            else {
                if ((this._otaResponse.otA_VehCancelRS.errors != null)
                    && (this._otaResponse.otA_VehCancelRS.errors.length > 0)) {
                    errors = this._otaResponse.otA_VehCancelRS.errors;
                }
            }
        }

        if (errors.length > 0) {
            //errors, so inform the user, and take them back to the previous page
            this._errorHeader = "Unable to cancel the booking";

            for (var otaErr in errors) {
                this._loadingErrorMsgs.push(errors[otaErr].shortText);
            }
        }
        else {
            //no errors
            this._confID = this._otaResponse.otA_VehCancelRS.vehCancelRSCore.uniqueID.uniqueID;
        }
    }

    private loadBusinessEvent_View() {
        this._title = "Your booking is " + this.statusDescription(this._be.otaVehRetResRS.vehRetResRSCore.vehReservation.reservationStatus) + " and cannot be modified";

        this._locationOut = this._be.locationOut.description;
        this._locationIn = this._be.locationIn.description;
        this._deliveryAddress = this._be.deliveryAddress;
        this._collectionAddress = this._be.collectionAddress;

        var dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        var timeOptions = { hour: 'numeric', minute: '2-digit' };
        this._dateTimeOut = ((this._be.dateTimeOut == null) ? "<unknown>" :
            this._be.dateTimeOut.toLocaleDateString(navigator.language, dateOptions)
            + " at " + this._be.dateTimeOut.toLocaleTimeString(navigator.language, timeOptions))
        this._dateTimeIn = ((this._be.dateTimeIn == null) ? "<unknown>" :
            this._be.dateTimeIn.toLocaleDateString(navigator.language, dateOptions)
            + " at " + this._be.dateTimeIn.toLocaleTimeString(navigator.language, timeOptions));

        this._be.locationOut.isAirport = this._be.locationOut.isAirport;
        this._arrivalNo = this._be.flightNumber;

        this._selectedVehicle = this._be.otaVehAvail;
        this._otaPricedEquips = this._be.otaVehAvail.vehAvailCore.pricedEquips.filter(eq => eq.charge.includedInEstTotalInd);
        this._otaPricedCoverages = this._be.otaVehAvail.vehAvailInfo.pricedCoverages;
        this._otaCharges = this._be.otaVehAvail.vehAvailCore.rentalRate.vehCharges;
        this._otaFees = this._be.otaVehAvail.vehAvailCore.fees.filter(f => f.includedInEstTotalInd);
        this._otaFeesNotIncluded = this._be.otaVehAvail.vehAvailCore.fees.filter(f => ((!f.includedInEstTotalInd) && (this._configDataService.showAdditionalFee(f))));


        //get the currently selected Coverage
        if (this._otaPricedCoverages.length > 0) {
            for (var i = 0; i < this._otaPricedCoverages.length; i++) {
                let cvr = this._otaPricedCoverages[i];

                if ((cvr.charge.includedInEstTotalInd) && (!cvr.charge.includedInRate)) {
                    this._otaPricedCoverage = cvr;
                    break;
                }
                else if (cvr.charge.includedInRate)
                    this._otaPricedCoverage = cvr;
            }
        }

        //this.totalCurrencyCost(this._be.otaVehAvail.vehAvailCore.totalCharge.currencyCode, this._be.otaVehAvail.vehAvailCore.totalCharge.estimatedTotalAmount);
        var ttlAmount = this._be.otaVehAvail.vehAvailCore.totalCharge.estimatedTotalAmount;
        if (this._be.payNow) {
            for (var chrg in this._otaCharges) {
                if (this._otaCharges[chrg].calculations != null) {
                    this._otaCharges[chrg].calculations.forEach(function (calc) {
                        ttlAmount = ttlAmount - (calc.total * 0.1);
                    });
                }
            }
        }
        this.totalCurrencyCost(this._be.otaVehAvail.vehAvailCore.totalCharge.currencyCode, ttlAmount);

        this._driverFirstName = this._be.rentingentity.firstName;
        this._driverLastName = this._be.rentingentity.principalName;
        //get the valid email Identification
        var eml = this._be.rentingentity.identList.find(x => ((x.identType == enumIdentType.idType_Email) && (x.identStatus == enumIdentStatus.idStatus_Valid)));
        if (eml != null) {
            this._driverEmail = eml.membershipNumber;
        }
        //get the Phone Number
        if (this._be.rentingentity.phoneList.length > 0) {
            this._driverPhoneNumber = this._be.rentingentity.phoneList[0].phoneNumber;
        }

        this._confID = this._be.otaVehRetResRS.vehRetResRSCore.vehReservation.vehSegmentCore.confID.confID;
        if (this._confID.trim().length == 0)
            this._confID = this._be.reference;
    }

    private createOTARequest(): OTA_Request {
        //construct the OTA Request object for the current business event
        let otaReq: OTA_Request = new OTA_Request(enumRequestType.reqType_Unknown);

        try {
            switch (this._be.eventAction) {
                case enumBusinessEventAction.beAction_Create:
                    otaReq = this.createOTARequest_Create();
                    break;

                case enumBusinessEventAction.beAction_Update:
                    otaReq = this.createOTARequest_Update();
                    break;

                case enumBusinessEventAction.beAction_Cancel:
                    otaReq = this.createOTARequest_Cancel();
                    break;
            }
        }
        catch (e) {
            console.log(e);
        }

        return otaReq;
    }

    private createOTARequest_Create(): OTA_Request {
        //construct the OTA Request object to create the current business event
        let otaReq: OTA_Request = new OTA_Request(enumRequestType.reqType_VehRes);

        try {
            const millisecondsPerMinute = 60000;

            if (this._be.dateTimeOut != null) {
                var offsetDateTimeOut: Date = new Date(this._be.dateTimeOut.valueOf() - (this._be.dateTimeOut.getTimezoneOffset() * millisecondsPerMinute));
                otaReq.otA_VehResRQ.vehResRQCore.vehRentalCore.pickUpDateTime = offsetDateTimeOut;
            }
            if (this._be.dateTimeIn != null) {
                var offsetDateTimeIn: Date = new Date(this._be.dateTimeIn.valueOf() - (this._be.dateTimeIn.getTimezoneOffset() * millisecondsPerMinute));
                otaReq.otA_VehResRQ.vehResRQCore.vehRentalCore.returnDateTime = offsetDateTimeIn;
            }

            otaReq.otA_VehResRQ.vehResRQCore.vehRentalCore.pickUpLocation.locationCode = this._be.locationOut.code;
            otaReq.otA_VehResRQ.vehResRQCore.vehRentalCore.returnLocation.locationCode = this._be.locationIn.code;

            otaReq.otA_VehResRQ.vehResRQInfo.arrivalDetails.number = this._be.flightNumber;

            otaReq.otA_VehResRQ.vehResRQCore.customer.primary.personName.givenName = this._be.rentingentity.firstName;
            otaReq.otA_VehResRQ.vehResRQCore.customer.primary.personName.surname = this._be.rentingentity.principalName;
            //otaReq.otA_VehResRQ.vehResRQCore.customer.primary.addresses[1].addressLines = this._be.rentingentity

            if (this._be.rentingentity.dateofBirth != null) {
                otaReq.otA_VehResRQ.vehResRQCore.customer.primary.birthDate = this._be.rentingentity.dateofBirth;
            }

            for (var i = 0; i < this._be.rentingentity.phoneList.length; i++) {
                let tel = this._be.rentingentity.phoneList[i];

                var otaTel: OTA_Telephone = new OTA_Telephone();
                otaTel.phoneNumber = tel.phoneNumber;
                otaTel.phoneTechType = "1";
                otaTel.phoneUseType = "1";
                otaTel.shareMarketInd = "No";

                otaReq.otA_VehResRQ.vehResRQCore.customer.primary.telephones.push(otaTel);
            }

            for (let eml of this._be.rentingentity.identList.filter(x => (x.identType == enumIdentType.idType_Email) && (x.identStatus == enumIdentStatus.idStatus_Valid))) {
                var otaEmail: OTA_Email = new OTA_Email();
                otaEmail.address = eml.membershipNumber;
                otaEmail.shareMarketInd = (eml.allowNotification ? "Yes" : "No");
                otaReq.otA_VehResRQ.vehResRQCore.customer.primary.emails.push(otaEmail);
            }

            for (let dl of this._be.rentingentity.identList.filter(x => (x.identType == enumIdentType.idType_DriversLicense) && (x.identStatus == enumIdentStatus.idStatus_Valid))) {
                var otaDoc: OTA_Document = new OTA_Document();
                otaDoc.docType = "4";
                otaDoc.docID = dl.membershipNumber;

                if (dl.expiry != null) {
                    otaDoc.expireDate = dl.expiry;
                }

                otaReq.otA_VehResRQ.vehResRQCore.customer.primary.documents.push(otaDoc);
            }

            if (this._be.rentingentity.address != null) {
                var otaAddress: OTA_Address = new OTA_Address();
                otaAddress.addressLines[0] = this._be.rentingentity.address.addressLine1;
                otaAddress.cityName = this._be.rentingentity.address.city;
                otaAddress.stateProv = this._be.rentingentity.address.state;
                otaAddress.postalCode = this._be.rentingentity.address.zipCode;
                otaAddress.type = this._be.rentingentity.address.addressTypeId;
                otaReq.otA_VehResRQ.vehResRQCore.customer.primary.addresses.push(otaAddress);
            }

            otaReq.otA_VehResRQ.vehResRQCore.vehPref.code = this._be.otaVehAvail.vehAvailCore.vehicle.code;
            otaReq.otA_VehResRQ.vehResRQCore.vehPref.codeContext = this._be.otaVehAvail.vehAvailCore.vehicle.codeContext;
            otaReq.otA_VehResRQ.vehResRQCore.driverType.code = 1;
            otaReq.otA_VehResRQ.vehResRQCore.driverType.age = this._be.ageRange.ageApplied;
            otaReq.otA_VehResRQ.vehResRQCore.driverType.quantity = 1;

            if ((this._be.promoCode != null)
                && (this._be.promoCode.trim().length > 0)) {

                switch (this._be.promotionType.promotionTypeId) {
                    case enumPromotionType.ptCustomerId:
                        //Customer ID
                        otaReq.otA_VehResRQ.vehResRQCore.rateQualifier.corpDiscountNmbr = this._be.promoCode.trim();
                        break;

                    case enumPromotionType.ptPromoCode:
                        //PromotionCode 
                        otaReq.otA_VehResRQ.vehResRQCore.rateQualifier.promotionCode = this._be.promoCode.trim();
                        break;
                }
            }

            for (let eq of this._be.otaVehAvail.vehAvailCore.pricedEquips.filter(x => x.requestedCount > 0)) {
                var otaSpecEq: OTA_SpecialEquipPref = new OTA_SpecialEquipPref();
                otaSpecEq.equipType = eq.equipment.equipType;
                otaSpecEq.quantity = eq.requestedCount;
                otaReq.otA_VehResRQ.vehResRQCore.specialEquipPrefs.push(otaSpecEq);
            }

            for (let cvr of this._be.otaVehAvail.vehAvailInfo.pricedCoverages.filter(x => x.selected)) {
                var otaCoverPref: OTA_CoveragePref = new OTA_CoveragePref();
                otaCoverPref.coverageType = cvr.coverageType;
                otaReq.otA_VehResRQ.vehResRQInfo.coveragePrefs.push(otaCoverPref);
            }

            if (this._be.hasDelivery) {
                let delivery: OTA_OffLocService = new OTA_OffLocService();
                delivery.type = "VehDelivery";

                var addrArray: string[] = this._be.deliveryAddress.trim().split("\n");
                for (var i = 0; i < addrArray.length; i++) {
                    if (addrArray[i].trim().length > 0)
                        delivery.address.addressLines.push(addrArray[i].trim());
                }

                otaReq.otA_VehResRQ.vehResRQInfo.offLocServices.push(delivery);
            }

            if (this._be.hasCollection) {
                let collection: OTA_OffLocService = new OTA_OffLocService();
                collection.type = "VehCollection";

                var addrArray: string[] = this._be.collectionAddress.trim().split("\n");
                for (var i = 0; i < addrArray.length; i++) {
                    if (addrArray[i].trim().length > 0)
                        collection.address.addressLines.push(addrArray[i].trim());
                }

                otaReq.otA_VehResRQ.vehResRQInfo.offLocServices.push(collection);
            }
        }
        catch (e) {
            console.log("ERROR: " + e);
        }

        return otaReq;
    }

    private createOTARequest_Update(): OTA_Request {
        //construct the OTA Request object to create the current business event
        let otaReq: OTA_Request = new OTA_Request(enumRequestType.reqType_VehModify);

        try {
            otaReq.otA_VehModifyRQ.uniqueID.uniqueID = this._be.reference;

            const millisecondsPerMinute = 60000;
            if (this._be.dateTimeOut != null) {
                var offsetDateTimeOut: Date = new Date(this._be.dateTimeOut.valueOf() - (this._be.dateTimeOut.getTimezoneOffset() * millisecondsPerMinute));
                otaReq.otA_VehModifyRQ.vehModifyRQCore.vehRentalCore.pickUpDateTime = offsetDateTimeOut;
            }
            if (this._be.dateTimeIn != null) {
                var offsetDateTimeIn: Date = new Date(this._be.dateTimeIn.valueOf() - (this._be.dateTimeIn.getTimezoneOffset() * millisecondsPerMinute));
                otaReq.otA_VehModifyRQ.vehModifyRQCore.vehRentalCore.returnDateTime = offsetDateTimeIn;
            }

            otaReq.otA_VehModifyRQ.vehModifyRQCore.vehRentalCore.pickUpLocation.locationCode = this._be.locationOut.code;
            otaReq.otA_VehModifyRQ.vehModifyRQCore.vehRentalCore.returnLocation.locationCode = this._be.locationIn.code;

            otaReq.otA_VehModifyRQ.vehModifyRQInfo.arrivalDetails.number = this._be.flightNumber;

            otaReq.otA_VehModifyRQ.vehModifyRQCore.customer.primary.personName.givenName = this._be.rentingentity.firstName;
            otaReq.otA_VehModifyRQ.vehModifyRQCore.customer.primary.personName.surname = this._be.rentingentity.principalName;
            if (this._be.rentingentity.dateofBirth != null) {
                otaReq.otA_VehModifyRQ.vehModifyRQCore.customer.primary.birthDate = this._be.rentingentity.dateofBirth;
            }
            otaReq.otA_VehModifyRQ.vehModifyRQCore.customer.primary.customerId = this._be.rentingentity.renterId;

            for (var i = 0; i < this._be.rentingentity.phoneList.length; i++) {
                let tel = this._be.rentingentity.phoneList[i];

                var otaTel: OTA_Telephone = new OTA_Telephone();
                otaTel.phoneNumber = tel.phoneNumber;
                otaTel.phoneTechType = "1";
                otaTel.phoneUseType = "1";
                otaTel.shareMarketInd = "No";

                otaReq.otA_VehModifyRQ.vehModifyRQCore.customer.primary.telephones.push(otaTel);
            }

            for (let eml of this._be.rentingentity.identList.filter(x => (x.identType == enumIdentType.idType_Email) && (x.identStatus == enumIdentStatus.idStatus_Valid))) {
                var otaEmail: OTA_Email = new OTA_Email();
                otaEmail.address = eml.membershipNumber;
                otaEmail.shareMarketInd = (eml.allowNotification ? "Yes" : "No");
                otaReq.otA_VehModifyRQ.vehModifyRQCore.customer.primary.emails.push(otaEmail);
            }

            for (let dl of this._be.rentingentity.identList.filter(x => (x.identType == enumIdentType.idType_DriversLicense) && (x.identStatus == enumIdentStatus.idStatus_Valid))) {
                var otaDoc: OTA_Document = new OTA_Document();
                otaDoc.docType = "4";
                otaDoc.docID = dl.membershipNumber;

                if (dl.expiry != null) {
                    otaDoc.expireDate = dl.expiry;
                }

                otaReq.otA_VehModifyRQ.vehModifyRQCore.customer.primary.documents.push(otaDoc);
            }

            if (this._be.rentingentity.address != null) {
                var otaAddress: OTA_Address = new OTA_Address();
                otaAddress.addressLines[0] = this._be.rentingentity.address.addressLine1;
                otaAddress.cityName = this._be.rentingentity.address.city;
                otaAddress.stateProv = this._be.rentingentity.address.state;
                otaAddress.postalCode = this._be.rentingentity.address.zipCode;
                otaAddress.type = this._be.rentingentity.address.addressTypeId;
                otaReq.otA_VehModifyRQ.vehModifyRQCore.customer.primary.addresses.push(otaAddress);
            }

            otaReq.otA_VehModifyRQ.vehModifyRQCore.vehPref.code = this._be.otaVehAvail.vehAvailCore.vehicle.code;
            otaReq.otA_VehModifyRQ.vehModifyRQCore.vehPref.codeContext = this._be.otaVehAvail.vehAvailCore.vehicle.codeContext;
            otaReq.otA_VehModifyRQ.vehModifyRQCore.driverType.code = 1;
            otaReq.otA_VehModifyRQ.vehModifyRQCore.driverType.age = this._be.ageRange.ageApplied;
            otaReq.otA_VehModifyRQ.vehModifyRQCore.driverType.quantity = 1;

            if ((this._be.promoCode != null)
                && (this._be.promoCode.trim().length > 0)) {

                switch (this._be.promotionType.promotionTypeId) {
                    case enumPromotionType.ptCustomerId:
                        //Customer ID
                        otaReq.otA_VehModifyRQ.vehModifyRQCore.rateQualifier.corpDiscountNmbr = this._be.promoCode.trim();
                        break;

                    case enumPromotionType.ptPromoCode:
                        //PromotionCode 
                        otaReq.otA_VehModifyRQ.vehModifyRQCore.rateQualifier.promotionCode = this._be.promoCode.trim();
                        break;
                }
            }

            for (let eq of this._be.otaVehAvail.vehAvailCore.pricedEquips.filter(x => x.requestedCount > 0)) {
                var otaSpecEq: OTA_SpecialEquipPref = new OTA_SpecialEquipPref();
                otaSpecEq.equipType = eq.equipment.equipType;
                otaSpecEq.quantity = eq.requestedCount;
                otaReq.otA_VehModifyRQ.vehModifyRQCore.specialEquipPrefs.push(otaSpecEq);
            }

            for (let cvr of this._be.otaVehAvail.vehAvailInfo.pricedCoverages.filter(x => x.selected)) {
                var otaCoverPref: OTA_CoveragePref = new OTA_CoveragePref();
                otaCoverPref.coverageType = cvr.coverageType;
                otaReq.otA_VehModifyRQ.vehModifyRQInfo.coveragePrefs.push(otaCoverPref);
            }

            if (this._be.hasDelivery) {
                let delivery: OTA_OffLocService = new OTA_OffLocService();
                delivery.type = "VehDelivery";

                var addrArray: string[] = this._be.deliveryAddress.trim().split("\n");
                for (var i = 0; i < addrArray.length; i++) {
                    if (addrArray[i].trim().length > 0)
                        delivery.address.addressLines.push(addrArray[i].trim());
                }

                otaReq.otA_VehModifyRQ.vehModifyRQInfo.offLocServices.push(delivery);
            }

            if (this._be.hasCollection) {
                let collection: OTA_OffLocService = new OTA_OffLocService();
                collection.type = "VehCollection";

                var addrArray: string[] = this._be.collectionAddress.trim().split("\n");
                for (var i = 0; i < addrArray.length; i++) {
                    if (addrArray[i].trim().length > 0)
                        collection.address.addressLines.push(addrArray[i].trim());
                }

                otaReq.otA_VehModifyRQ.vehModifyRQInfo.offLocServices.push(collection);
            }
        }
        catch (e) {
            console.log(e);
        }

        return otaReq;
    }

    private createOTARequest_Cancel(): OTA_Request {
        //construct the OTA Request object to Cancel the current business event
        let otaReq: OTA_Request = new OTA_Request(enumRequestType.reqType_VehCancel);
        let confID: string = "";

        try {
            confID = this._be.otaVehRetResRS.vehRetResRSCore.vehReservation.vehSegmentCore.confID.confID;
            if ((confID == null)
                || (confID.trim().length == 0))
                confID = this._be.reference;

            otaReq.otA_VehCancelRQ.vehCancelRQCore.uniqueID.uniqueID = confID;
        }
        catch (e) {
            console.log(e);
        }

        return otaReq;
    }

    private save(): boolean {
        this._beDataService.setBusinessEventForStep(this._currentStep, this._be);
        return true;
    }

    private goToPrevious() {
        let step: string = this._beDataService.invalidateStep(this._previousStep);
        this.navigateTo(step);
    }

    public goToStart() {
        if (this.save()) {
            // Navigate to the Business Event Itinerary page
            this.navigateTo(this._firstStep);
        }
    }

    private navigateTo(step: string) {
        //navigate to the given page
        if (step.trim().length > 0)
            this._router.navigate(['/' + step]);
    }

    //
    //helper methods
    //
    private vehicleImageURL(vehAvail: OTA_VehAvail): string {
        var imageURL = ""

        if ((vehAvail != null)
            && (vehAvail.vehAvailCore != null)
            && (vehAvail.vehAvailCore.vehicle != null)) {

            //imageURL = vehAvail.vehAvailCore.vehicle.vehicleImageRef;
            imageURL = vehAvail.vehAvailCore.vehicle.code + ".png";

            if (imageURL.length > 0)
                imageURL = this._baseVehImgURL + imageURL;
        }

        return imageURL;
    }

    private onImgError(event) {
        event.target.src = this._baseVehImgURL + "Coming_Soon.png";
    }

    private formatChargeTotal(chg: OTA_Charge, payNow: boolean = false): string {
        let cost: CurrencyAmount = new CurrencyAmount();

        cost.amount = OTA_Charge.chargeTotal(chg);
        if (payNow) {
            cost.amount = cost.amount * 0.9;
        }

        cost.currencyCode = chg.currencyCode;

        return this._configDataService.formatCurrencyAmount(cost);
    }

    private formatFeeTotal(fee: OTA_Fee): string {
        let cost: CurrencyAmount = new CurrencyAmount();
        cost.amount = OTA_Fee.feeTotal(fee);
        cost.currencyCode = fee.currencyCode;

        return this._configDataService.formatCurrencyAmount(cost);
    }

    private formatPricedEquipTotal(eq: OTA_PricedEquip): string {
        let cost: CurrencyAmount = new CurrencyAmount();
        cost.amount = OTA_PricedEquip.pricedEquipTotal(eq);
        cost.currencyCode = eq.charge.currencyCode;

        return this._configDataService.formatCurrencyAmount(cost);
    }

    private formatCurrencyTotal(currAmount: CurrencyAmount): string {
        return this._configDataService.formatCurrencyAmount(currAmount);
    }

    private totalCurrencyCost(currencyCode: string, amount: number) {
        var currAmnt = this._totalCosts.find(x => (x.currencyCode == currencyCode));
        if (currAmnt == null) {
            currAmnt = new CurrencyAmount()
            currAmnt.currencyCode = currencyCode;
            currAmnt.amount = 0;
            this._totalCosts.push(currAmnt);
        }
        currAmnt.amount = currAmnt.amount + amount;
    }

    private statusDescription(status: string): string {
        //return a modified description of the given status
        //most of the time, no modification is required, but some statuses do
        //eg. status 'Close' need to be displayed as 'Closed'
        let desc: string = status;

        switch (status.trim().toLocaleUpperCase()) {
            case "CLOSE":
                desc = "Closed";
        }

        return desc;
    }
}
